import { useEffect, useState } from "react";
import axios from "axios";
import SeoHead from "@/components/public/SeoHead";
import DevisForm from "@/components/public/DevisForm";
import { Phone, Mail, MapPin, Clock, MessageCircle, Truck, CreditCard } from "lucide-react";
import Breadcrumb from "@/components/public/Breadcrumb";
import FadeIn from "@/components/public/FadeIn";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactPage() {
  const [zones, setZones] = useState([]);

  useEffect(() => {
    axios.get(`${API}/zones-deplacement`).then(r => setZones(r.data));
  }, []);

  return (
    <div data-testid="contact-page">
      <SeoHead
        title="Contact et devis gratuit"
        description="Contactez Nettoyage Expert pour un devis gratuit en 2h. Tél: 024 539 10 33. WhatsApp: +41 78 620 28 54. Nettoyage professionnel en Suisse romande."
        path="/contact"
        breadcrumbs={[
          { name: "Accueil", url: "/" },
          { name: "Contact" }
        ]}
      />
      <Breadcrumb items={[{ name: "Accueil", url: "/" }, { name: "Contact" }]} />
      {/* Hero */}
      <section className="bg-[#336DBF] py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-semibold text-white tracking-tight">
            Contactez Nettoyage Expert en Suisse romande
          </h1>
          <p className="mt-4 text-white/80 text-lg max-w-2xl">
            Devis gratuit en 2 heures. Appelez-nous, ecrivez-nous ou remplissez le formulaire.
          </p>
        </div>
      </section>

      {/* Contact Grid */}
      <section className="py-16 sm:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 lg:gap-16">
            {/* Form */}
            <div className="lg:col-span-3">
              <FadeIn>
                <h2 className="text-2xl font-semibold text-[#062133] tracking-tight mb-6">
                  Demandez votre devis gratuit
                </h2>
                <div className="bg-white border border-[#062133]/10 rounded-lg p-6 sm:p-8 shadow-sm">
                  <DevisForm source="direct" />
                </div>
              </FadeIn>
            </div>

            {/* Info */}
            <div className="lg:col-span-2">
              <FadeIn delay={200}>
                <h2 className="text-2xl font-semibold text-[#062133] tracking-tight mb-6">
                  Nos coordonnees
                </h2>
                <div className="space-y-6">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-md bg-[#336DBF]/10 flex items-center justify-center shrink-0">
                      <MapPin className="w-5 h-5 text-[#336DBF]" />
                    </div>
                    <div>
                      <p className="font-semibold text-[#062133] text-sm">Adresse</p>
                      <p className="text-sm text-[#062133]/60 mt-0.5">Rue Jean-André Venel 4<br />1400 Yverdon-les-Bains</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-md bg-[#336DBF]/10 flex items-center justify-center shrink-0">
                      <Phone className="w-5 h-5 text-[#336DBF]" />
                    </div>
                    <div>
                      <p className="font-semibold text-[#062133] text-sm">Téléphone</p>
                      <a href="tel:+41245391033" className="text-sm text-[#336DBF] font-medium hover:underline mt-0.5 block">024 539 10 33</a>
                      <p className="text-xs text-[#062133]/50 mt-1">
                        Appels pris 24h/24 par notre agent <a href="https://talk-flow.ai" target="_blank" rel="noopener" className="text-[#336DBF] hover:underline">Talk-Flow</a> — rendez-vous confirmés via <a href="https://www.loomy-crm.com" target="_blank" rel="noopener" className="text-[#336DBF] hover:underline">Loomy CRM</a>.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-md bg-[#336DBF]/10 flex items-center justify-center shrink-0">
                      <Mail className="w-5 h-5 text-[#336DBF]" />
                    </div>
                    <div>
                      <p className="font-semibold text-[#062133] text-sm">Email</p>
                      <a href="mailto:info@nettoyage-expert.ch" className="text-sm text-[#336DBF] font-medium hover:underline mt-0.5 block">info@nettoyage-expert.ch</a>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-md bg-[#336DBF]/10 flex items-center justify-center shrink-0">
                      <Clock className="w-5 h-5 text-[#336DBF]" />
                    </div>
                    <div>
                      <p className="font-semibold text-[#062133] text-sm">Horaires</p>
                      <div className="text-sm text-[#062133]/60 mt-0.5 space-y-0.5">
                        <p>Lun-Mer : 08:00 - 18:00</p>
                        <p>Jeu : 08:00 - 18:00</p>
                        <p>Ven : 08:00 - 17:00</p>
                        <p>Sam : 09:00 - 12:30</p>
                      </div>
                    </div>
                  </div>

                  {/* WhatsApp */}
                  <a
                    href="https://wa.me/41786202854"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 w-full px-6 py-3.5 bg-[#25D366] text-white font-semibold rounded-md hover:bg-[#20BD5C] transition-colors"
                    data-testid="contact-whatsapp"
                  >
                    <MessageCircle size={18} />
                    Ecrivez-nous sur WhatsApp
                  </a>
                </div>
              </FadeIn>
            </div>
          </div>
        </div>
      </section>

      {/* Zones d'intervention */}
      {zones.length > 0 && (
        <section className="py-16 sm:py-24 bg-[#F4F6F8]" data-testid="zones-deplacement-section">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <FadeIn>
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-md bg-[#336DBF]/10 flex items-center justify-center">
                  <Truck className="w-5 h-5 text-[#336DBF]" />
                </div>
                <h2 className="text-2xl font-semibold text-[#062133] tracking-tight">
                  Zones d'intervention
                </h2>
              </div>
              <p className="text-sm text-[#062133]/60 mb-8 ml-[52px]">
                Nous intervenons dans toute la Suisse romande
              </p>

              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                {[...zones].sort((a, b) => a.zone.localeCompare(b.zone)).map(z => (
                  <div key={z.zone} className="bg-white border border-[#062133]/10 rounded-lg px-4 py-3 text-sm text-[#062133]">
                    <MapPin size={14} className="inline mr-1.5 text-[#336DBF]" />{z.zone}
                  </div>
                ))}
              </div>
            </FadeIn>
          </div>
        </section>
      )}

      {/* Modes de paiement */}
      <section className="py-12 sm:py-16 bg-white" data-testid="payment-methods-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeIn>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-md bg-[#336DBF]/10 flex items-center justify-center">
                <CreditCard className="w-5 h-5 text-[#336DBF]" />
              </div>
              <h2 className="text-xl font-semibold text-[#062133] tracking-tight">
                Modes de paiement acceptés
              </h2>
            </div>
            <div className="bg-white border border-[#062133]/10 rounded-lg p-6 shadow-sm">
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-600 text-sm font-bold">CHF</div>
                  <div>
                    <p className="text-sm font-semibold text-[#062133]">Cash</p>
                    <p className="text-xs text-[#062133]/60">Pas de supplément</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-[#336DBF]/10 flex items-center justify-center text-[#336DBF] text-xs font-bold">TW</div>
                  <div>
                    <p className="text-sm font-semibold text-[#062133]">Twint</p>
                    <p className="text-xs text-[#062133]/60">Supplément de 3%</p>
                  </div>
                </div>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>
    </div>
  );
}
